import { DateRecurrence, RecurrenceUnit } from '../../objects/critical-dates';

/**
 * Recurrence math for Important Dates.
 *
 * Deliberately NOT cron, even though the user-facing control is modelled on the
 * Scheduled Reports schedule dialog. Cron cannot say "every year on August 18"
 * or "every 3 months" without contortion, and it does not need to here: the
 * reminder job already wakes once a day, so recurrence only has to answer "what
 * is the next date after this one?" — which is date arithmetic. Borrowing the
 * cron machinery from hc-all-orgs-scheduled-ops would be the expensive wrong
 * answer; borrowing its UX is the point.
 *
 * Everything here is pure so the backend roll-forward can implement the same
 * rules against the same definitions (see
 * docs/backend-prompt-2026-09-13-recurring-important-dates.md).
 */

/** A repeat option offered in the UI, and the (unit, interval) it stores as. */
export interface RecurrencePreset {
  label: string;
  unit: RecurrenceUnit;
  interval: number;
}

/**
 * The offered repeats. Quarterly and semi-annual are month intervals rather
 * than units of their own — one less concept to carry, and "every 3 months" is
 * exactly what a quarterly contract review means.
 */
export const RECURRENCE_PRESETS: RecurrencePreset[] = [
  { label: 'Weekly',        unit: 'week',  interval: 1 },
  { label: 'Every 2 weeks', unit: 'week',  interval: 2 },
  { label: 'Monthly',       unit: 'month', interval: 1 },
  { label: 'Quarterly',     unit: 'month', interval: 3 },
  { label: 'Semi-annually', unit: 'month', interval: 6 },
  { label: 'Annually',      unit: 'year',  interval: 1 }
];

/** The default for a date the user has just switched Repeat on for. */
export function defaultRecurrence(): DateRecurrence {
  return { enabled: true, unit: 'year', interval: 1, endsOn: null };
}

/**
 * Re-anchor a recurrence to a date — what the UI does whenever a person
 * configures the repeat or moves the Important Date.
 *
 * Anchoring is not bookkeeping: it is what stops a month-end series from
 * drifting. See `DateRecurrence.anchorDate`.
 */
export function anchorTo(rec: DateRecurrence, anchorIso: string | null | undefined): DateRecurrence {
  return { ...rec, anchorDate: anchorIso ?? null, occurrenceCount: 0 };
}

/**
 * Human label for any (unit, interval) pair — including one that matches no
 * preset, so a value written by the backend or by a future UI still reads
 * correctly instead of showing a blank select.
 */
export function recurrenceLabel(rec: DateRecurrence | null | undefined): string {
  if (!rec?.enabled) return '';
  const preset = RECURRENCE_PRESETS.find(p => p.unit === rec.unit && p.interval === rec.interval);
  if (preset) return preset.label;
  const n = Math.max(1, Math.round(rec.interval || 1));
  return `Every ${n} ${rec.unit}${n === 1 ? '' : 's'}`;
}

/**
 * Add one interval to a date.
 *
 * Month and year steps CLAMP the day: Jan 31 + 1 month is Feb 28 (or 29), not
 * Mar 3, which is what a naive `setMonth` produces — it overflows into the next
 * month and would silently walk a month-end reminder forward through the
 * calendar, a little further every time it fires.
 *
 * Arithmetic uses the LOCAL getters/setters, matching how the rest of the app
 * reads these values: the datepicker writes local midnight as an ISO instant
 * ('2026-08-18T07:00:00.000Z' is Aug 18 in California), and every display path
 * parses it back the same way. Working in UTC here would shift the day for
 * exactly the orgs whose dates are stored that way.
 */
export function addInterval(date: Date, unit: RecurrenceUnit, interval: number): Date {
  const n = Math.max(1, Math.round(interval || 1));
  const next = new Date(date.getTime());

  if (unit === 'week') {
    next.setDate(next.getDate() + 7 * n);
    return next;
  }

  const months = unit === 'year' ? 12 * n : n;
  const dayOfMonth = next.getDate();
  next.setDate(1);                      // park on the 1st so the month step cannot overflow
  next.setMonth(next.getMonth() + months);
  const lastDayOfTargetMonth = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
  next.setDate(Math.min(dayOfMonth, lastDayOfTargetMonth));
  return next;
}

/**
 * The occurrence after `currentIso` (the date the record carries now), as an ISO
 * string — or null when the series has ended: repeat off, unparseable date, or
 * the next one falls past `endsOn`. `endsOn` is inclusive, so an end date equal
 * to the next occurrence still runs that occurrence.
 *
 * Counted from `recurrence.anchorDate` where there is one — `anchor + interval ×
 * (occurrenceCount + 1)` — rather than one step from the current date, so a
 * month-end series cannot drift (see `DateRecurrence.anchorDate`). Falls back to
 * stepping from the current date when there is no anchor (a record written
 * before anchoring existed) or when the anchor math would land on or before the
 * current date, which means someone moved the date out from under the anchor.
 */
export function nextOccurrence(
  currentIso: string | null | undefined,
  rec: DateRecurrence | null | undefined
): string | null {
  if (!rec?.enabled || !currentIso) return null;

  const current = new Date(currentIso);
  if (isNaN(current.getTime())) return null;

  let next = addInterval(current, rec.unit, rec.interval);

  if (rec.anchorDate) {
    const anchor = new Date(rec.anchorDate);
    if (!isNaN(anchor.getTime())) {
      const steps = Math.max(0, Math.round(rec.occurrenceCount ?? 0)) + 1;
      const fromAnchor = addInterval(anchor, rec.unit, rec.interval * steps);
      if (fromAnchor.getTime() > current.getTime()) next = fromAnchor;
    }
  }

  if (rec.endsOn) {
    const ends = new Date(rec.endsOn);
    if (!isNaN(ends.getTime())) {  // an unparseable end date must not cancel the series
      // End of the end-day, so a time-of-day difference cannot drop a valid occurrence.
      ends.setHours(23, 59, 59, 999);
      if (next.getTime() > ends.getTime()) return null;
    }
  }

  return next.toISOString();
}

/**
 * One-line plain-English description — the counterpart of the schedule dialog's
 * `getScheduleSummary()`. Returns '' when the date does not repeat.
 */
export function describeRecurrence(
  rec: DateRecurrence | null | undefined,
  currentIso?: string | null
): string {
  if (!rec?.enabled) return '';

  const base = `Repeats ${recurrenceLabel(rec).toLowerCase()}`;
  const next = nextOccurrence(currentIso, rec);

  if (next) {
    const ending = rec.endsOn ? `, ending ${formatDay(rec.endsOn)}` : '';
    return `${base} — the next one after this falls on ${formatDay(next)}${ending}`;
  }

  // No next occurrence but repeat is on: either we have no date to count from
  // yet, or the end date has already cut the series off. Say which.
  if (currentIso && rec.endsOn) {
    return `${base} — this is the last occurrence before it ends on ${formatDay(rec.endsOn)}`;
  }
  return `${base} — set the Important Date to see when the next one falls`;
}

/** 'Aug 18, 2027'. Parsed as an instant, formatted locally — same as the rest of the app. */
function formatDay(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(d);
}
